const selectors = {
  componentsCollectionJSON: '[data-components-collection-json]',
  atcButtonLabel: '.fc-add-to-cart-button .fc-button-label',
  warning: '[data-product-customizer-warning]',
};

class ProductCustomizer {
  /**
   * ProductCustomizer constructor (#fluidConfigure element wrapper)
   * @param {jQuery | HTMLElement} el - Main element
   */
  constructor(el) {
    this.settings = {};
    this.name = 'productCustomizer';

    this.$el = $(el);

    // Bail if no element provided.
    if (!this.$el || this.$el === undefined) {
      console.warn(`[${this.name}] - $el required to initialize`);
      return;
    }

    this.customProductURL = this.$el.data('custom-product-url');
    this.customProductVariantID = this.$el.data('custom-product-variant-id');
    this.$warning = $(selectors.warning, this.$el);
    this.componentsCollectionHandle = this.$el.attr('data-custom-product-components');

    // Create property to store reference to component variants
    // Will be replaced with an array of objects if the call succeeds.
    this.componentVariants = [];

    // Use component collection handle to fetch product
    this._fetchComponentProducts(this.componentsCollectionHandle)
      .then((variants) => {
        this.componentVariants = variants;
      })
      .catch((e) => {
        // If something goes wrong, set componentVariants to null.
        // We'll use the non-array value to determine if the fetch failed
        console.warn(`Unable to fetch components collection with handle: ${this.componentsCollectionHandle}`);
        this.componentVariants = null;
      });

    /* Function calls
     * ================================================================== */

    // The configureID functions need to be defined in the window scope.
    // If an uuid search param is seen in URL, configureEditCart will run.
    // The uuid can be anything at the moment. It is the orderItemId passed in this function.
    window.configureEditCart = (recipeId, orderItemId) => {
      this._addBundleToCart(recipeId, true);
    };

    window.configureAddToCart = (recipeId) => {
      this._addBundleToCart(recipeId, false);
    };
  }

  /**
   * Fetches the collection containining this custom product's
   * components.  Necessary for the add-to-cart method to function.
   * @param {String} collectionHandle
   * @returns {Promise} resolves with the array of component products present in the component collection
   */

  getCollectionByHandle(handle) {
    return fetch(`/collections/` + handle + `?view=custom-feed-json`).then((response) => response.json());
  }

  async _fetchComponentProducts(collectionHandle) {
    let componentsCollection = null;
    try {
      componentsCollection = await this.getCollectionByHandle(collectionHandle);
    } catch (e) {
      throw new Error(e);
    }

    return componentsCollection?.products;
  }

  /**
   * Method to add an item to cart.  Is exposed to, and called by, the
   * Fluid Configure app
   * @param {Object} recipeId - an object from Fluid Configure containing details on the custom built product
   * @param {Boolean} editMode
   * @returns {Null}
   */
  _addBundleToCart(recipeId, editMode) {
    if (typeof recipeId === 'undefined') return;

    // We only need to look for these component 'types' to pair with Shopify products
    const componentKeysWithProducts = ['band', 'case', 'buckle', 'face', 'second'];

    // Destructure recipe data
    const componentsData = recipeId.extended_attributes;
    const componentSelections = { ...recipeId.custom.selections };
    const image = `${recipeId.images.view_1}&width=300&height=300`;
    const url = `${this.customProductURL}?recipe=${recipeId.id}`;
    const timeStamp = recipeId.creation_date;
    //const price = recipeId.prices[window.Shopify.currency.active];
    // const fontSize = componentsData.font_size.value.info.description;
    // Dont need this mapping as font_size value is available in main selections attribute
    // NIX-778 mapping directly to custom json attributes

    // Original font_size value 'Aa' should be swapped to 'Small/Big'
    // componentSelections.font_size = fontSize;
    // Don't need this mapping as font_size value is available in main selections attribute
    // NIX-778 mapping directly to custom json attributes

    // Change custom engraving property from personalization_1line to personalization_line1
    if (componentSelections.hasOwnProperty('personalization_1line')) {
      componentSelections.personalization_line1 = componentSelections.personalization_1line;
      delete componentSelections.personalization_1line;
    }

    // `extended_attributes` contains a *lot* of data. We only care about the
    // k/v's that need to be mapped to Shopify products (see arr above).
    // Reduce out an object where key = component type, and value = SKU
    const componentSkusInBundle = Object.keys(componentsData).reduce((obj, key) => {
      if (componentKeysWithProducts.includes(key)) {
        const data = componentsData[key];
        const sku = data?.value?.info?.vendorId || null;
        if (sku) {
          return { ...obj, [key]: sku };
        }
      }
      return obj;
    }, {});

    // Cross reference the list of component skus with the variants list
    // fetched from this custom product's components collection.
    const variantsInBundle = this.componentVariants.filter((variant) =>
      Object.values(componentSkusInBundle).includes(variant.sku)
    );

    // Make sure all variants are accounted for before continuing.
    const allVariantsAvailable =
      variantsInBundle.length === Object.values(componentSkusInBundle).length &&
      variantsInBundle.every((v) => v.available);

    // Construct array of items to add to Shopify cart
    const items = [
      // Component Variants
      ...variantsInBundle.map((v) => ({
        quantity: 1,
        id: v.id,
        properties: {
          _time_stamp: timeStamp,
          _recipe_id: recipeId.id,
          _parts_recipe_id: recipeId.id,
        },
      })),
      // Parent Product
      {
        quantity: 1,
        id: this.customProductVariantID,
        properties: {
          _time_stamp: timeStamp,
          _recipe_id: recipeId.id,
          _image: image,
          _url: url,
          _resource_url: recipeId.uri,
          //_price: price,
          ...componentSelections,
          ...Object.keys(componentSkusInBundle).reduce(
            (obj, key) => ({
              ...obj,
              [`_${key}_SKU`]: componentSkusInBundle[key],
            }),
            {}
          ),
        },
      },
    ];

    // If we're missing variants, error
    // Note, we can be missing items because (a) they're out of stock,
    // or (b) the SKU isn't present in the components array.  Either way,
    // we show the same error.
    if (!allVariantsAvailable) {
      // Filter for the unavailable items
      const unavailableItems = Object.keys(componentSkusInBundle).filter((k) => {
        const sku = componentSkusInBundle[k];
        return !variantsInBundle.some((v) => v.sku === sku && v.available);
      });

      // Show warning detailing the unavailable component types
      // TODO - This is way too subtle and needs to be more prominent.
      // Leaving as is for now due to time constraints
      // this.$warning.text(
      // `${window.theme.strings.unavailable}: ${unavailableItems}`
      // );
      this.updateCtaState('unavailable');
      setTimeout(() => {
        this.updateCtaState(editMode ? 'edit' : 'add');
      }, 3000);

      return;
    }

    // Update CTA text before triggering add method
    this.updateCtaState(editMode ? 'editing' : 'adding');

    // The configureEditCart function runs this code block.
    // Get saved data from localforage and use it to delete the watch bundle
    // being edited from cart, then add a new bundle.
    if (editMode) {
      // Construct object of keys (line-item keys) and values (0) to hand off to cart API.
      const itemsBeingEdited = this.getItemsBeingEdited();
      const qtyByKey = itemsBeingEdited
        ? itemsBeingEdited?.reduce((obj, likey) => {
            return {
              ...obj,
              [likey]: 0,
            };
          }, {})
        : {};

      // Remove original items, add new items, redirect to cart
      this.updateLineItemQuantitiesByKey(qtyByKey)
        .then(() => CartAPI.addItemsToCart(items))
        .fail(() => {
          console.warn('something went wrong...');
        })
        .always(() => {
          this.redirectToCart();
        });

      // Return early to prevent normal add from taking place
      return;
    }

    // The configureAddToCart function runs when editMode is false (by default).
    // A watch and the watch components are added to cart as a bundle.
    this.addItemsToCart(items).always(() => {
      this.updateCtaState(editMode ? 'edit' : 'add');
      this.redirectToCart();
    });
  }

  /**
   * Updates the CTA label in response to add/edit events
   * @param {String} state ['add', 'adding', 'edit', 'editing', 'unavailable']
   */
  updateCtaState(state) {
    let newLabel = '';
    switch (state) {
      case 'adding':
        newLabel = 'Adding...';
        break;
      case 'edit':
        newLabel = 'Update cart';
        break;
      case 'editing':
        newLabel = 'Updating...';
        break;
      case 'unavailable':
        newLabel = 'Unavailable';
        break;
      default:
        newLabel = 'Add to cart';
    }

    this.$el.find(selectors.atcButtonLabel).html(newLabel);
  }

  addItemsToCart(items, emitCartEvents) {
    const promise = $.Deferred();

    $.ajax({
      type: 'post',
      dataType: 'json',
      url: '/cart/add.js',
      data: {
        items,
      },
      success: (data) => {
        promise.resolve(data);
      },
      error: (data) => {
        promise.reject({
          message: 'The quantity you entered is not available.',
        });
        if (emitCartEvents) {
          const event = $.Event('addFail.ajaxFormManager', { data });
          $window.trigger(event);
        }
      },
    });

    return promise;
  }

  /**
   * Updates multiple quantities of items in the cart by index.
   * @param {Object} newQuantities - an object of k/v, where key = line-item key, and value = quantity.
   */
  updateLineItemQuantitiesByKey(newQuantities) {
    const promise = $.Deferred();

    // if cart isnt fetched, reject.
    if (!this.cart || typeof newQuantities !== 'object') {
      return promise.reject();
    }

    // Format quantities array
    const keysToUpdate = Object.keys(newQuantities);
    const qtyArr = this.cart.items.map((item) => {
      const key = keysToUpdate.find((k) => k === item.key);
      return key ? newQuantities[key] : item.quantity;
    });

    // Execute call
    $.ajax({
      type: 'post',
      dataType: 'json',
      url: '/cart/update.js',
      data: { updates: qtyArr },
      success: (cart) => {
        promise.resolve(data);
      },
      error: () => {
        const data = {
          message: 'Something went wrong.',
        };
        promise.reject(data);
      },
    });

    return promise;
  }

  /**
   * Redirects to the cart page
   */
  redirectToCart() {
    window.location = '/cart';
  }

  parseQueryString(url) {
    const queryString = url.split('?')[1];
    if (!queryString) return {};

    const params = {};
    queryString.split('&').forEach((param) => {
      const [key, value] = param.split('=');
      params[key] = value ? decodeURIComponent(value.replace(/\+/g, ' ')) : '';
    });
    return params;
  }

  /**
   * Retrieves the cart line-item keys being edited from the URL
   * so that they can be removed before adding the new.
   * @returns {Array of Strings} array of line item keys
   */
  getItemsBeingEdited() {
    const queryString = window.location.search;
    const query = this.parseQueryString(queryString);
    const itemsBeingEdited = query.editing;
    return Array.isArray(itemsBeingEdited) ? itemsBeingEdited : [itemsBeingEdited];
  }

  /**
   * Provided an array of products, this method reduces out an array of all variants
   * @param {Array of Objects} products
   * @returns {Array of Objects}
   */
  _formatVariantsArray(products) {
    // Format an array of all variants.  Append product title as a prop
    const reducedProds = products.reduce(
      (acc, p) => (Array.isArray(p?.variants) ? [...acc, ...p.variants.map((v) => ({ ...v, name: p.title }))] : acc),
      []
    );

    console.log(reducedProds);
    return reducedProds;
  }
}

const prodSelectors = {
  productDetail: '[data-product-detail]',
  productCustomizer: '[data-product-customizer]',
  productRQ: '[data-prodcut-rq]',
};

new ProductCustomizer(prodSelectors.productCustomizer);
